import { SECOND_MS } from '@calm/web-common/dist/time';
import { useAudio } from '@rhg/hooks/use-audio';
import { useAudioTiming } from '@rhg/hooks/use-audio-timing';
import { useSmoothedTime } from '@rhg/hooks/use-smoothed-time';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    Slider,
    Typography,
    useTheme,
    useMediaQuery,
    Theme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { displayTime } from '@rhg/utils/media';
import { useIntl } from 'react-intl';
import { AudioTimelineVariant } from '../types';

const styleTimingContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
};

const styleDurationText = {
    minWidth: '48px',
};

const styleElapsedText = {
    ...styleDurationText,
    textAlign: 'right',
};

const styleSmallTimeline = {
    mt: 4,
};

const styleTimingSlider = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};
const styleTimingSliderMinimal = { mx: 0 };
const styleTimingControl = {
    width: '100%',
    // This is due to MUI adding a 20px padding around the slider for touch targets
    // causing layout issues in our design. We need to offset this padding to ensure the slider fits correctly within its container.
    '@media (pointer: coarse)': {
        mt: -4,
        mb: -2,
    },
    '& .MuiSlider-thumb': {
        width: 8,
        height: 8,
        // MUI's own SliderThumb bakes in `transition: left/box-shadow/bottom`
        // unconditionally. useSmoothedTime repositions the thumb every
        // animation frame (~60-100Hz) — far faster than that transition can
        // settle — so each frame restarts it before it paints, freezing the
        // thumb in place. Disable it; JS now owns the motion. EVE-13651.
        transition: 'none',
        color: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'info.main' : 'info.light',
        '::before': {
            borderRadius: '50%',
            content: '""',
            width: 12,
            height: 12,
            backgroundColor: (theme: Theme) =>
                theme.palette.mode === 'light' ? 'info.main' : 'info.light',
            filter: 'opacity(20%)',
        },
    },
    '& .MuiSlider-track': {
        // Same reasoning as the thumb above — MUI's SliderTrack also bakes
        // in a `left/width/bottom/height` transition unconditionally.
        transition: 'none',
        color: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'info.main' : 'info.light',
        height: 4,
    },
    '& .MuiSlider-rail': {
        color: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'info.main' : 'info.light',
        height: 4,
    },
};

const styleTimingSliderControlMinimal = {
    py: 0,
    '& .MuiSlider-thumb': {
        width: 0,
        height: 0,
    },
};

export interface AudioTimelineProps {
    variant?: AudioTimelineVariant;
}
export function AudioTimeline({
    variant = AudioTimelineVariant.Large,
}: AudioTimelineProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        controls: { setTime, playbackRate },
    } = useAudio();
    // The scrubber genuinely needs the ~4Hz timing, so it subscribes to the
    // dedicated timing context. EVE-12668.
    const { timingState } = useAudioTiming();
    // Interpolates between the ~4Hz ticks so the thumb moves smoothly, while
    // still snapping instantly on seek/track-change. EVE-13651.
    const smoothedCurrentTime = useSmoothedTime({
        currentTime: timingState.currentTime,
        isPaused: timingState.isPaused,
        playbackRate,
        duration: timingState.duration,
    });

    return (
        <>
            <Box
                sx={sxAppend(
                    styleTimingContainer,
                    variant === AudioTimelineVariant.Small && styleSmallTimeline
                )}
            >
                {variant !== AudioTimelineVariant.Minimal && !isMobile && (
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        sx={styleElapsedText}
                        aria-label={formatMessage(
                            playerMessages['player.current_time'],
                            {
                                time: displayTime(timingState.currentTime),
                            }
                        )}
                    >
                        {displayTime(timingState.currentTime)}
                    </Typography>
                )}
                <Box
                    sx={sxAppend(
                        styleTimingSlider,
                        variant === AudioTimelineVariant.Minimal &&
                            styleTimingSliderMinimal
                    )}
                >
                    <Slider
                        sx={sxAppend(
                            styleTimingControl,
                            variant === AudioTimelineVariant.Minimal &&
                                styleTimingSliderControlMinimal
                        )}
                        value={smoothedCurrentTime}
                        max={timingState.duration}
                        aria-label={formatMessage(
                            playerMessages['player.seek_slider']
                        )}
                        getAriaValueText={(sliderValue) =>
                            formatMessage(
                                playerMessages['player.current_time'],
                                {
                                    time: displayTime(sliderValue),
                                }
                            )
                        }
                        step={timingState.duration / SECOND_MS}
                        size="medium"
                        onChangeCommitted={(_, newTime) => {
                            setTime(newTime as number);
                        }}
                    />
                </Box>
                {variant !== AudioTimelineVariant.Minimal && !isMobile && (
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        sx={styleDurationText}
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.duration'],
                            {
                                time: displayTime(timingState.duration),
                            }
                        )}
                    >
                        {displayTime(timingState.duration)}
                    </Typography>
                )}
            </Box>
            {variant !== AudioTimelineVariant.Minimal && isMobile && (
                <Box sx={styleTimingContainer}>
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.current_time'],
                            {
                                time: displayTime(timingState.currentTime),
                            }
                        )}
                    >
                        {displayTime(timingState.currentTime)}
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.duration'],
                            {
                                time: displayTime(timingState.duration),
                            }
                        )}
                    >
                        {displayTime(timingState.duration)}
                    </Typography>
                </Box>
            )}
        </>
    );
}
